'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { toast } from '@warfighter/ui'
import posthog from 'posthog-js'
import {
  calculateCivilianPay,
  calculateVeteranPay,
  deleteScenario,
  saveScenario,
  lookupCareerMap,
  searchCareerJobs,
  getLocalWages,
  type CareerMapResult,
  type CareerMatch,
  type CareerJobSearchResult,
  type AdzunaJob,
  type GeoWageContext,
} from '@/app/actions'
import { createBrowserSupabaseClient } from '@/lib/supabaseClient'
import UnifiedResults, { extractJobKeywords, classifyMatch } from '@/app/components/calculators/CareerMapResults'
import {
  ArrowRight,
  Award,
  ChevronDown,
  Clock,
  DollarSign,
  FolderOpen,
  Loader2,
  MapPin,
  Sparkles,
  ShieldCheck,
  Trash2,
  User,
  Users
} from 'lucide-react'

export type CalculatorMode = 'separation' | 'veteran'

export type CalculatorFormData = {
  rank: string
  yearsOfService: number
  currentZip: string
  currentState: string
  targetZip: string
  targetState: string
  hasDependents: boolean
  isStateTaxExempt: boolean
  includeHealthInsurance: boolean
  additionalIncome: {
    label: string
    amount: number
    cadence: 'monthly' | 'annual'
    taxFree: boolean
  }[]
  currentAdditionalIncome: {
    label: string
    amount: number
    cadence: 'monthly' | 'annual'
    taxFree: boolean
  }[]
  currentSalary?: number
  vaCompMonthly?: number
}

type CalculatorProps = {
  onResult?: (result: any, input: any) => void
  initialValues?: Partial<CalculatorFormData>
  mode?: CalculatorMode
}

export default function Calculator({ onResult, initialValues, mode = 'separation' }: CalculatorProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const supabase = useMemo(() => createBrowserSupabaseClient(), [])
  const [result, setResult] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [saving, setSaving] = useState(false)
  const [topPriority, setTopPriority] = useState(false)
  const [lastInput, setLastInput] = useState<any>(null)
  const [savedScenarios, setSavedScenarios] = useState<any[]>([])
  const [scenariosLoading, setScenariosLoading] = useState(false)
  const [showScenarioMenu, setShowScenarioMenu] = useState(false)
  const [selectedScenarioTitle, setSelectedScenarioTitle] = useState('')
  const scenarioMenuRef = useRef<HTMLDivElement | null>(null)
  const resultsRef = useRef<HTMLDivElement | null>(null)
  const initialValuesApplied = useRef(false)

  // Career map state
  const [careerResult, setCareerResult] = useState<CareerMapResult | null>(null)
  const [careerJobResults, setCareerJobResults] = useState<CareerJobSearchResult[]>([])
  const [careerJobsLoading, setCareerJobsLoading] = useState(false)
  const [careerLoading, setCareerLoading] = useState(false)
  const [likedJobUrls, setLikedJobUrls] = useState<Set<string>>(new Set())
  const [userId, setUserId] = useState<string | null>(null)
  const profileBranchRef = useRef<string>('')
  const profileMosRef = useRef<string>('')
  const profileSpecialistSkillsRef = useRef<string[]>([])
  const isVeteran = mode === 'veteran'
  const currentLocationLabel = isVeteran ? 'Current Location' : 'Current Duty Station'
  const targetLocationLabel = isVeteran ? 'New Location' : 'Next Adventure'
  const salaryHeadline = isVeteran
    ? 'Salary Needed in Target Location'
    : 'Civilian Salary Needed'
  const takeHomeLabel = isVeteran ? 'Current Take-Home' : 'Military Take-Home'
  const targetSalaryLabel = isVeteran ? 'Target Salary Needed' : 'Civilian Salary Needed'
  const taxLabel = isVeteran ? 'Tax Impact (Estimate)' : 'New Tax Bill'

  // Simple Form State
  const [formData, setFormData] = useState<CalculatorFormData>({
    rank: 'E-5',
    yearsOfService: 6,
    currentZip: '92101', // San Diego default
    currentState: 'CA',
    targetZip: '35801',  // Huntsville default
    targetState: 'AL',
    hasDependents: true,
    isStateTaxExempt: false,
    includeHealthInsurance: false,
    currentSalary: 0,
    vaCompMonthly: 0,
    additionalIncome: [] as {
      label: string
      amount: number
      cadence: 'monthly' | 'annual'
      taxFree: boolean
    }[],
    currentAdditionalIncome: [] as {
      label: string
      amount: number
      cadence: 'monthly' | 'annual'
      taxFree: boolean
    }[]
  })

  const targetZipParam = searchParams.get('targetZip')

  useEffect(() => {
    if (!targetZipParam) return
    const cleaned = targetZipParam.trim()
    if (!/^\d{5}$/.test(cleaned)) return
    setFormData((prev) => ({
      ...prev,
      targetZip: cleaned
    }))
  }, [targetZipParam])

  const resolveStateFromZip = async (
    zip: string,
    field: 'currentState' | 'targetState'
  ) => {
    const trimmed = zip.trim()
    if (trimmed.length !== 5) return
    const { data } = await supabase
      .from('zip_codes')
      .select('state_abbr')
      .eq('zip', trimmed)
      .maybeSingle()
    const state = data?.state_abbr?.trim().toUpperCase()
    if (!state) return
    setFormData((prev) => {
      if (field === 'currentState' && prev.currentZip !== trimmed) return prev
      if (field === 'targetState' && prev.targetZip !== trimmed) return prev
      return prev[field] === state ? prev : { ...prev, [field]: state }
    })
  }

  const buildCalculationPayload = (input: CalculatorFormData) => {
    const normalizeIncomeItems = (
      items: CalculatorFormData['additionalIncome'] | CalculatorFormData['currentAdditionalIncome']
    ) =>
      items
        .map((item) => ({
          label: item.label?.trim() ?? '',
          amount: Number.isFinite(item.amount) ? Number(item.amount) : 0,
          cadence: item.cadence ?? 'monthly',
          taxFree: Boolean(item.taxFree)
        }))
        .filter((item) => item.amount > 0)

    if (isVeteran) {
      return {
        currentSalary: Number(input.currentSalary ?? 0),
        currentZip: input.currentZip,
        currentState: input.currentState,
        targetZip: input.targetZip,
        targetState: input.targetState,
        vaCompMonthly: Number(input.vaCompMonthly ?? 0),
        includeHealthInsurance: input.includeHealthInsurance
      }
    }
    return {
      ...input,
      additionalIncome: normalizeIncomeItems(input.additionalIncome),
      currentAdditionalIncome: normalizeIncomeItems(input.currentAdditionalIncome)
    }
  }

  const runCalculation = async (input: CalculatorFormData) => {
    setLoading(true)
    try {
      const payload = buildCalculationPayload(input)
      const data = isVeteran
        ? await calculateVeteranPay(payload as any)
        : await calculateCivilianPay(payload as any)
      setResult(data)
      setLastInput(payload)
      // Auto-scroll to results after calculation
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
      posthog.capture('calculator_run', {
        mode: isVeteran ? 'veteran' : 'civilian',
        zip: 'targetZip' in payload ? payload.targetZip : undefined,
        rank: 'rank' in payload ? payload.rank : undefined,
      })
      if (onResult) onResult(data, payload)

      // Trigger career map lookup (non-blocking) if we have branch + MOS from profile
      if (!isVeteran && profileBranchRef.current && profileMosRef.current) {
        triggerCareerMap(input.rank, input.targetZip)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast.error(message)
    }
    setLoading(false)
  }

  /** Trigger the career map lookup using the user's profile branch/MOS and the calculator's rank + target ZIP. */
  const triggerCareerMap = async (rank: string, targetZip: string) => {
    const branch = profileBranchRef.current
    const mosCode = profileMosRef.current
    if (!branch || !mosCode || !rank) return

    setCareerLoading(true)
    setCareerResult(null)
    setCareerJobResults([])
    try {
      const data = await lookupCareerMap({ branch, mosCode, rank })

      const cleanZip = targetZip.trim().replace(/\D/g, '').slice(0, 5)
      let geoContext: GeoWageContext | undefined
      if (cleanZip.length === 5) {
        const socCodes = [...new Set(data.matches.map((m) => m.soc_code))]
        geoContext = await getLocalWages(cleanZip, socCodes)

        if (geoContext.wages.length > 0) {
          const wageMap = new Map(geoContext.wages.map((w) => [w.soc_code, w]))
          for (const match of data.matches) {
            const localWage = wageMap.get(match.soc_code)
            if (localWage) {
              match.local_salary = {
                a_pct25: localWage.a_pct25,
                a_median: localWage.a_median,
                a_pct75: localWage.a_pct75,
                data_source: localWage.data_source,
                tot_emp: localWage.tot_emp,
              }
            }
          }
        }
        data.geoContext = geoContext
      }

      setCareerResult(data)

      // Kick off Adzuna job search
      setCareerJobsLoading(true)
      const grouped: Record<string, CareerMatch[]> = { direct: [], strong: [], growth: [] }
      for (const m of data.matches) grouped[classifyMatch(m)].push(m)

      const civilianSearches: Array<{ title: string; socCode: string; tier: 'direct' | 'strong' | 'growth' }> = []
      for (const m of (grouped.direct ?? []).slice(0, 2)) civilianSearches.push({ title: m.soc_title, socCode: m.soc_code, tier: 'direct' })
      for (const m of (grouped.strong ?? []).slice(0, 2)) civilianSearches.push({ title: m.soc_title, socCode: m.soc_code, tier: 'strong' })
      for (const m of (grouped.growth ?? []).slice(0, 1)) civilianSearches.push({ title: m.soc_title, socCode: m.soc_code, tier: 'growth' })

      const adzunaLocation = geoContext?.city && geoContext?.state
        ? `${geoContext.city}, ${geoContext.state}`
        : undefined

      let personalizationKeywords: string[] | undefined
      if (userId) {
        const { data: likedRows } = await supabase
          .from('liked_jobs')
          .select('job_title, job_company')
          .eq('user_id', userId)
          .order('liked_at', { ascending: false })
          .limit(10)
        if (likedRows && likedRows.length > 0) {
          const allKw: string[] = []
          for (const row of likedRows) allKw.push(...extractJobKeywords({ title: row.job_title, company: row.job_company }))
          const freq = new Map<string, number>()
          for (const kw of allKw) freq.set(kw, (freq.get(kw) ?? 0) + 1)
          personalizationKeywords = [...freq.entries()].sort((a, b) => b[1] - a[1]).map(([kw]) => kw).slice(0, 6)
        }
      }

      searchCareerJobs({
        militaryTitle: data.mosTitle,
        mosCode: data.mosCode,
        civilianSearches,
        location: adzunaLocation,
        cbsaName: geoContext?.parent_cbsa ?? null,
        personalizationKeywords,
        specialistSkills: profileSpecialistSkillsRef.current.length > 0
          ? profileSpecialistSkillsRef.current
          : undefined,
      })
        .then((jobs) => setCareerJobResults(jobs))
        .catch(() => setCareerJobResults([]))
        .finally(() => setCareerJobsLoading(false))
    } catch {
      // Career map errors are non-fatal — the salary calculation is the primary result
    }
    setCareerLoading(false)
  }

  /** Toggle like on a job card */
  const handleToggleLike = async (job: AdzunaJob) => {
    if (!userId) return
    const url = job.redirect_url
    const wasLiked = likedJobUrls.has(url)

    setLikedJobUrls((prev) => {
      const next = new Set(prev)
      if (wasLiked) next.delete(url); else next.add(url)
      return next
    })

    try {
      if (wasLiked) {
        await supabase.from('liked_jobs').delete().eq('user_id', userId).eq('job_redirect_url', url)
      } else {
        await supabase.from('liked_jobs').insert({
          user_id: userId,
          job_redirect_url: url,
          job_title: job.title ?? '',
          job_company: job.company ?? null,
          job_location: job.location ?? null,
        })
      }
    } catch {
      setLikedJobUrls((prev) => {
        const next = new Set(prev)
        if (wasLiked) next.add(url); else next.delete(url)
        return next
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isVeteran && (!formData.currentSalary || formData.currentSalary <= 0)) {
      toast.warning('Enter your current annual salary.')
      return
    }
    await runCalculation(formData)
  }

  useEffect(() => {
    if (!initialValues || initialValuesApplied.current) return
    setFormData((prev) => ({ ...prev, ...initialValues }))
    initialValuesApplied.current = true
  }, [initialValues])

  useEffect(() => {
    const loadProfileDefaults = async () => {
      const { data } = await supabase.auth.getSession()
      if (!data.session) return

      const uid = data.session.user.id
      setUserId(uid)

      setScenariosLoading(true)
      const [{ data: profile }, { data: scenarios }, { data: likedRows }] = await Promise.all([
        supabase
          .from('profiles')
          .select('rank, years_of_service, service_branch, mos_code, specialist_skills')
          .eq('id', uid)
          .single(),
        supabase
          .from('saved_scenarios')
          .select('id, title, inputs, created_at, top_priority')
          .eq('user_id', uid)
          .order('created_at', { ascending: false }),
        supabase
          .from('liked_jobs')
          .select('job_redirect_url')
          .eq('user_id', uid),
      ])

      if (profile) {
        setFormData((prev) => ({
          ...prev,
          rank: profile.rank ?? prev.rank,
          yearsOfService:
            typeof profile.years_of_service === 'number'
              ? profile.years_of_service
              : prev.yearsOfService
        }))
        // Store branch/MOS for career map lookup after calculation
        profileBranchRef.current = (profile.service_branch ?? '').toLowerCase().replace(/[\s-]+/g, '_')
        profileMosRef.current = profile.mos_code ?? ''
        profileSpecialistSkillsRef.current = typeof profile.specialist_skills === 'string' && profile.specialist_skills.trim()
          ? profile.specialist_skills.split(',').map((s: string) => s.trim()).filter(Boolean)
          : []
      }

      if (likedRows) {
        setLikedJobUrls(new Set(likedRows.map((r: { job_redirect_url: string }) => r.job_redirect_url)))
      }

      setSavedScenarios(scenarios ?? [])
      setScenariosLoading(false)
    }

    loadProfileDefaults()
  }, [supabase])

  useEffect(() => {
    if (!formData.currentZip) return
    const handle = window.setTimeout(() => {
      resolveStateFromZip(formData.currentZip, 'currentState')
    }, 400)
    return () => window.clearTimeout(handle)
  }, [formData.currentZip, supabase])

  useEffect(() => {
    if (!formData.targetZip) return
    const handle = window.setTimeout(() => {
      resolveStateFromZip(formData.targetZip, 'targetState')
    }, 400)
    return () => window.clearTimeout(handle)
  }, [formData.targetZip, supabase])

  useEffect(() => {
    if (!showScenarioMenu) return
    const handleClickOutside = (event: MouseEvent) => {
      if (!scenarioMenuRef.current) return
      if (scenarioMenuRef.current.contains(event.target as Node)) return
      setShowScenarioMenu(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showScenarioMenu])

  const handleIncomeChange = (
    index: number,
    updates: Partial<{ label: string; amount: number; cadence: 'monthly' | 'annual'; taxFree: boolean }>
  ) => {
    setFormData((prev) => {
      const next = [...prev.additionalIncome]
      next[index] = { ...next[index], ...updates }
      return { ...prev, additionalIncome: next }
    })
  }

  const handleAddIncome = () => {
    setFormData((prev) => ({
      ...prev,
      additionalIncome: [
        ...prev.additionalIncome,
        { label: '', amount: 0, cadence: 'monthly', taxFree: false }
      ]
    }))
  }

  const handleRemoveIncome = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      additionalIncome: prev.additionalIncome.filter((_, idx) => idx !== index)
    }))
  }

  const handleCurrentIncomeChange = (
    index: number,
    updates: Partial<{ label: string; amount: number; cadence: 'monthly' | 'annual'; taxFree: boolean }>
  ) => {
    setFormData((prev) => {
      const next = [...prev.currentAdditionalIncome]
      next[index] = { ...next[index], ...updates }
      return { ...prev, currentAdditionalIncome: next }
    })
  }

  const handleAddCurrentIncome = () => {
    setFormData((prev) => ({
      ...prev,
      currentAdditionalIncome: [
        ...prev.currentAdditionalIncome,
        { label: '', amount: 0, cadence: 'monthly', taxFree: false }
      ]
    }))
  }

  const handleRemoveCurrentIncome = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      currentAdditionalIncome: prev.currentAdditionalIncome.filter((_, idx) => idx !== index)
    }))
  }

  const handleSaveScenarioWithPriority = async (isTopPriority: boolean) => {
    if (!lastInput || !result) {
      toast.info('Run a calculation first.')
      return
    }
    const resolvedTargetZip = lastInput.targetZip ?? formData.targetZip
    const resolvedCurrentZip = lastInput.currentZip ?? formData.currentZip
    const resolvedTargetState = lastInput.targetState ?? formData.targetState

    if (!resolvedTargetZip) {
      toast.warning('Target zip is required.')
      return
    }

    setSaving(true)
    const { data } = await supabase.auth.getSession()
    const session = data.session
    const accessToken = session?.access_token
    if (!session || !accessToken) {
      setSaving(false)
      router.push('/login')
      return
    }
    const userId = session.user.id

    try {
      const zipList = [resolvedTargetZip, resolvedCurrentZip]
        .filter((zip) => typeof zip === 'string' && zip.trim().length === 5)
        .map((zip) => zip.trim())

      const { data: zipRows } = zipList.length
        ? await supabase.from('zip_codes').select('zip, city').in('zip', zipList)
        : { data: [] as { zip: string; city: string | null }[] }

      const findCity = (zip: string) =>
        zipRows?.find((row) => row.zip === zip)?.city ?? null

      const resolvedCity = findCity(resolvedTargetZip)?.trim() || null
      const resolvedCurrentCity = findCity(resolvedCurrentZip)?.trim() || null
      const titleBase = resolvedCity ?? lastInput.targetZip
      const suggestedTitle = isTopPriority
        ? `${titleBase} — My New Home`
        : `Move to ${titleBase}`
      const scenarioTitle = window.prompt('Name this scenario', suggestedTitle)
      if (!scenarioTitle || !scenarioTitle.trim()) {
        setSaving(false)
        return
      }

      await saveScenario({
        accessToken,
        title: scenarioTitle.trim(),
        targetCity: resolvedCity ?? undefined,
        targetZip: resolvedTargetZip,
        currentCity: resolvedCurrentCity ?? undefined,
        currentZip: resolvedCurrentZip,
        topPriority: isTopPriority,
        calculatedSalaryNeed: result?.civilian?.annualSalaryNeeded,
        targetState: resolvedTargetState,
        currentBah: typeof result?.military?.bah === 'number' ? result.military.bah : undefined,
        targetBah: typeof result?.civilian?.targetBah === 'number' ? result.civilian.targetBah : undefined,
        hourlyEquivalent:
          typeof result?.civilian?.hourlyEquivalent === 'string'
            ? Number(result.civilian.hourlyEquivalent)
            : typeof result?.civilian?.hourlyEquivalent === 'number'
            ? result.civilian.hourlyEquivalent
            : undefined,
        inputs: lastInput
      })
      posthog.capture('scenario_saved', {
        topPriority: isTopPriority,
        zip: lastInput?.zipCode,
      })
      setTopPriority(false)
      setScenariosLoading(true)
      const { data: refreshed } = await supabase
        .from('saved_scenarios')
        .select('id, title, inputs, created_at, top_priority')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
      setSavedScenarios(refreshed ?? [])
      setScenariosLoading(false)
      if (isTopPriority) {
        toast.success('Scenario saved as your new home!')
        router.push('/dashboard')
      } else {
        toast.success('Scenario saved!')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast.error(message)
    }
    setSaving(false)
  }

  const handleLoadScenario = async (scenarioId: string) => {
    const scenario = savedScenarios.find((item) => item.id === scenarioId)
    if (!scenario) return

    const nextInput = {
      ...formData,
      ...(scenario.inputs ?? {}),
      hasDependents:
        typeof scenario.inputs?.hasDependents === 'boolean'
          ? scenario.inputs.hasDependents
          : formData.hasDependents,
      isStateTaxExempt: Boolean(scenario.inputs?.isStateTaxExempt),
      includeHealthInsurance: Boolean(scenario.inputs?.includeHealthInsurance),
      currentSalary:
        typeof scenario.inputs?.currentSalary === 'number'
          ? scenario.inputs.currentSalary
          : formData.currentSalary,
      vaCompMonthly:
        typeof scenario.inputs?.vaCompMonthly === 'number'
          ? scenario.inputs.vaCompMonthly
          : formData.vaCompMonthly,
      additionalIncome: Array.isArray(scenario.inputs?.additionalIncome)
        ? scenario.inputs.additionalIncome
        : [],
      currentAdditionalIncome: Array.isArray(scenario.inputs?.currentAdditionalIncome)
        ? scenario.inputs.currentAdditionalIncome
        : []
    }

    setFormData(nextInput)
    posthog.capture('scenario_loaded', { scenarioId })
    await runCalculation(nextInput)
    setSelectedScenarioTitle(scenario.title ?? '')
    setShowScenarioMenu(false)
  }

  const handleDeleteScenario = async (scenarioId: string) => {
    const scenario = savedScenarios.find((item) => item.id === scenarioId)
    if (!scenario) return
    if (!window.confirm(`Delete "${scenario.title}"? This cannot be undone.`)) return

    setSaving(true)
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) {
      setSaving(false)
      router.push('/login')
      return
    }

    try {
      await deleteScenario({ accessToken, scenarioId })
      setSavedScenarios((prev) => prev.filter((item) => item.id !== scenarioId))
      if (selectedScenarioTitle === scenario.title) {
        setSelectedScenarioTitle('')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast.error(message)
    }
    setSaving(false)
  }

  return (
    <div className="mx-auto max-w-4xl rounded-3xl bg-white p-8 shadow-2xl shadow-blue-100 md:p-12">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Run Your Scenario</h1>
          <p className="text-sm text-slate-500">
            Compare your military compensation against civilian reality.
          </p>
        </div>
        <div className="relative" ref={scenarioMenuRef}>
          <button
            type="button"
            onClick={() => setShowScenarioMenu((prev) => !prev)}
            disabled={scenariosLoading || savedScenarios.length === 0}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
          >
            <FolderOpen className="h-4 w-4" />
            {scenariosLoading ? 'Loading...' : 'Load Saved Scenario'}
          </button>
          {showScenarioMenu && savedScenarios.length > 0 && (
            <div className="absolute right-0 z-10 mt-3 w-64 rounded-2xl border border-slate-100 bg-white p-2 shadow-xl">
              {savedScenarios.map((scenario) => (
                <div
                  key={`scenario-${scenario.id}`}
                  className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <button
                    type="button"
                    onClick={() => handleLoadScenario(scenario.id)}
                    className="flex-1 truncate text-left font-medium"
                  >
                    {scenario.title}
                  </button>
                  {scenario.top_priority && (
                    <span className="text-teal-500" title="Top priority" aria-label="Top priority">
                      ★
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDeleteScenario(scenario.id)}
                    className="text-slate-400 transition hover:text-red-500"
                    aria-label={`Delete ${scenario.title}`}
                    title={scenario.top_priority ? 'Cannot delete top priority' : 'Delete'}
                    disabled={scenario.top_priority}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-8">
        {isVeteran ? (
          <section className="space-y-4">
            <p className="text-sm font-semibold text-slate-700">Your Income</p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Current Salary (Annual)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="number"
                    min="0"
                    className="h-12 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-sm font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.currentSalary ?? ''}
                    onChange={(e) =>
                      setFormData({ ...formData, currentSalary: Number(e.target.value) })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  VA Disability Compensation (Monthly)
                </label>
                <div className="relative">
                  <ShieldCheck className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="number"
                    min="0"
                    className="h-12 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-sm font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.vaCompMonthly ?? ''}
                    onChange={(e) =>
                      setFormData({ ...formData, vaCompMonthly: Number(e.target.value) })
                    }
                  />
                </div>
                <p className="text-xs text-slate-400">
                  Add your monthly tax-free VA benefit if applicable.
                </p>
              </div>
            </div>
          </section>
        ) : (
          <section className="space-y-4">
            <p className="text-sm font-semibold text-slate-700">The Operator</p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Rank
                </label>
                <div className="relative">
                  <Award className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <select
                    className="h-12 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-sm font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.rank}
                    onChange={(e) => setFormData({ ...formData, rank: e.target.value })}
                  >
                    <option value="E-1">E-1</option>
                    <option value="E-2">E-2</option>
                    <option value="E-3">E-3</option>
                    <option value="E-4">E-4</option>
                    <option value="E-5">E-5</option>
                    <option value="E-6">E-6</option>
                    <option value="E-7">E-7</option>
                    <option value="E-8">E-8</option>
                    <option value="E-9">E-9</option>
                    <option value="W-1">W-1</option>
                    <option value="W-2">W-2</option>
                    <option value="W-3">W-3</option>
                    <option value="O-1">O-1</option>
                    <option value="O-2">O-2</option>
                    <option value="O-3">O-3</option>
                    <option value="O-4">O-4</option>
                    <option value="O-5">O-5</option>
                    <option value="O-6">O-6</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Years of Service
                </label>
                <div className="relative">
                  <Clock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="number"
                    className="h-12 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-sm font-semibold text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.yearsOfService}
                    onChange={(e) =>
                      setFormData({ ...formData, yearsOfService: Number(e.target.value) })
                    }
                  />
                </div>
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Dependents
                </label>
                <div className="grid grid-cols-2 rounded-full bg-slate-100 p-1">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, hasDependents: false })}
                    className={`flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                      !formData.hasDependents
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500'
                    }`}
                  >
                    <User className="h-4 w-4" />
                    Single
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, hasDependents: true })}
                    className={`flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                      formData.hasDependents
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500'
                    }`}
                  >
                    <Users className="h-4 w-4" />
                    With Dependents
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="space-y-4">
          <p className="text-sm font-semibold text-slate-700">The Move</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {currentLocationLabel}
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter ZIP code"
                  className="h-14 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-base font-semibold text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.currentZip}
                  onChange={(e) => setFormData({ ...formData, currentZip: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-center">
              <div className="h-8 w-px bg-slate-200" />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {targetLocationLabel}
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-600" />
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter ZIP code"
                  className="h-14 w-full rounded-xl border border-transparent bg-slate-100 pl-11 pr-3 text-base font-semibold text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.targetZip}
                  onChange={(e) => setFormData({ ...formData, targetZip: e.target.value })}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <button
            type="button"
            onClick={() => setShowAdvanced((prev) => !prev)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-slate-900"
          >
            <ChevronDown
              className={`h-4 w-4 transition ${showAdvanced ? 'rotate-180' : ''}`}
            />
            {showAdvanced
              ? 'Hide Advanced Settings (Taxes, Deductions)'
              : 'Show Advanced Settings (Taxes, Deductions)'}
          </button>

          {showAdvanced && (
            <div className="space-y-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
              {!isVeteran && (
                <label className="flex items-start gap-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    checked={formData.isStateTaxExempt}
                    onChange={(e) =>
                      setFormData({ ...formData, isStateTaxExempt: e.target.checked })
                    }
                  />
                  <span>
                    Are You Currently State Income Tax Exempt?
                    <span className="mt-1 block text-xs text-slate-500">
                      This overrides current state taxes.
                    </span>
                  </span>
                </label>
              )}
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  checked={formData.includeHealthInsurance}
                  onChange={(e) =>
                    setFormData({ ...formData, includeHealthInsurance: e.target.checked })
                  }
                />
                <span>
                  Include Civilian Health Insurance Cost?
                  <span className="mt-1 block text-xs text-slate-500">
                    Adds $7,200/year to the target.
                  </span>
                </span>
              </label>
              {!isVeteran && (
                <div className="space-y-6">
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Current income add-ons
                    </p>
                    {formData.currentAdditionalIncome.length === 0 && (
                      <p className="text-xs text-slate-500">
                        Add current pay you&apos;ll need to replace. This increases the required salary.
                      </p>
                    )}
                    {formData.currentAdditionalIncome.map((income, index) => (
                      <div key={index} className="rounded-xl bg-white p-3 shadow-sm">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <input
                            type="text"
                            placeholder="(e.g. BAS, Hazard, Language, Special Pay, Bonus)"
                            className="h-12 w-full rounded-xl border border-transparent bg-slate-100 px-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={income.label}
                            onChange={(e) =>
                              handleCurrentIncomeChange(index, { label: e.target.value })
                            }
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Amount"
                            className="h-12 w-full rounded-xl border border-transparent bg-slate-100 px-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={Number.isNaN(income.amount) ? '' : income.amount}
                            onChange={(e) =>
                              handleCurrentIncomeChange(index, { amount: Number(e.target.value) })
                            }
                          />
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              checked={income.taxFree}
                              onChange={(e) =>
                                handleCurrentIncomeChange(index, { taxFree: e.target.checked })
                              }
                            />
                            Tax free
                          </label>
                          <select
                            className="h-10 rounded-xl border border-transparent bg-slate-100 px-3 text-sm text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={income.cadence}
                            onChange={(e) =>
                              handleCurrentIncomeChange(index, {
                                cadence: e.target.value as 'monthly' | 'annual'
                              })
                            }
                          >
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleRemoveCurrentIncome(index)}
                            className="ml-auto text-xs font-semibold text-red-500 hover:text-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddCurrentIncome}
                      className="w-full rounded-xl border border-dashed border-slate-200 bg-white py-3 text-sm font-semibold text-slate-600 hover:border-slate-300"
                    >
                      + Add Current Income
                    </button>
                  </div>

                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Other income offsets
                    </p>
                    {formData.additionalIncome.length === 0 && (
                      <p className="text-xs text-slate-500">
                        Add additional income sources to reduce the required salary.
                      </p>
                    )}
                    {formData.additionalIncome.map((income, index) => (
                      <div key={index} className="rounded-xl bg-white p-3 shadow-sm">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <input
                            type="text"
                            placeholder="(e.g. VA-Disability, Retirement Pay, Side Hustles)"
                            className="h-12 w-full rounded-xl border border-transparent bg-slate-100 px-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={income.label}
                            onChange={(e) => handleIncomeChange(index, { label: e.target.value })}
                          />
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Amount"
                            className="h-12 w-full rounded-xl border border-transparent bg-slate-100 px-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={Number.isNaN(income.amount) ? '' : income.amount}
                            onChange={(e) =>
                              handleIncomeChange(index, { amount: Number(e.target.value) })
                            }
                          />
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              checked={income.taxFree}
                              onChange={(e) =>
                                handleIncomeChange(index, { taxFree: e.target.checked })
                              }
                            />
                            Tax free
                          </label>
                          <select
                            className="h-10 rounded-xl border border-transparent bg-slate-100 px-3 text-sm text-slate-700 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={income.cadence}
                            onChange={(e) =>
                              handleIncomeChange(index, {
                                cadence: e.target.value as 'monthly' | 'annual'
                              })
                            }
                          >
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleRemoveIncome(index)}
                            className="ml-auto text-xs font-semibold text-red-500 hover:text-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddIncome}
                      className="w-full rounded-xl border border-dashed border-slate-200 bg-white py-3 text-sm font-semibold text-slate-600 hover:border-slate-300"
                    >
                      + Add Additional Income
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <button
          type="submit"
          disabled={loading}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] text-sm font-semibold text-[var(--accent-contrast)] shadow-lg shadow-[var(--accent)]/30 transition hover:bg-[var(--accent-hover)]"
        >
          {loading ? 'Calculating...' : 'Calculate Scenario'}
          <ArrowRight className="h-4 w-4" />
        </button>
      </form>

      {result && (
        <div ref={resultsRef} className="mt-10 rounded-3xl border border-slate-200 bg-slate-50 p-6">
          <div className="text-center">
            <p className="text-xs uppercase tracking-wide text-slate-500">{salaryHeadline}</p>
            <p className="mt-2 text-4xl font-extrabold text-slate-900">
              ${result.civilian.annualSalaryNeeded.toLocaleString()}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              ${Number(result.civilian.annualSalaryNeeded / 2080).toFixed(2)} / hr
            </p>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{takeHomeLabel}</span>
                <span>${result.military.annualTakeHome.toLocaleString()}</span>
              </div>
              <div className="mt-2 h-2 w-full rounded-full bg-white">
                <div
                  className="h-2 rounded-full bg-slate-400"
                  style={{
                    width: `${
                      Math.max(
                        10,
                        Math.round(
                          (result.military.annualTakeHome /
                            Math.max(result.military.annualTakeHome, result.civilian.annualSalaryNeeded)) *
                            100
                        )
                      )
                    }%`
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{targetSalaryLabel}</span>
                <span>${result.civilian.annualSalaryNeeded.toLocaleString()}</span>
              </div>
              <div className="mt-2 h-2 w-full rounded-full bg-white">
                <div
                  className="h-2 rounded-full bg-[var(--accent)]"
                  style={{
                    width: `${
                      Math.max(
                        10,
                        Math.round(
                          (result.civilian.annualSalaryNeeded /
                            Math.max(result.military.annualTakeHome, result.civilian.annualSalaryNeeded)) *
                            100
                        )
                      )
                    }%`
                  }}
                />
              </div>
            </div>
            {result.civilian.annualSalaryNeeded > result.military.annualTakeHome && (
              <p className="text-xs font-semibold text-slate-700">
                {isVeteran ? 'Target salary is' : 'Civilian salary must be'}{' '}
                {(
                  ((result.civilian.annualSalaryNeeded - result.military.annualTakeHome) /
                    result.military.annualTakeHome) *
                  100
                ).toFixed(1)}
                % higher {isVeteran ? 'than your current take-home.' : 'to match.'}
              </p>
            )}
          </div>

          <div className="mt-6 space-y-2 text-sm text-slate-600">
            <p className="flex justify-between">
              <span>{takeHomeLabel}:</span>
              <span className="font-semibold">${result.military.annualTakeHome.toLocaleString()}</span>
            </p>
            {result.military.currentAdditionalIncomeTotal > 0 && (
              <p className="flex justify-between text-xs text-slate-500 pl-2">
                <span>Includes add-ons:</span>
                <span>${result.military.currentAdditionalIncomeTotal.toLocaleString()}/yr</span>
              </p>
            )}
            {typeof result.civilian.targetBah === 'number' && result.civilian.targetBah > 0 && (
              <p className="flex justify-between">
                <span>Target BAH (Monthly):</span>
                <span className="font-semibold">${result.civilian.targetBah.toLocaleString()}</span>
              </p>
            )}
            {result.civilian.incomeOffsets && result.civilian.incomeOffsets.totalAnnualOffset > 0 && (
              <p className="flex justify-between text-emerald-600">
                <span>Income offsets applied:</span>
                <span className="font-semibold">
                  -${result.civilian.incomeOffsets.totalAnnualOffset.toLocaleString()}/yr
                </span>
              </p>
            )}
            {result.civilian.taxBurden > 0 && (
              <p className="flex justify-between text-rose-500">
                <span>Est. Tax on Civilian Salary:</span>
                <span className="font-semibold">${result.civilian.taxBurden.toLocaleString()}</span>
              </p>
            )}
            {result.civilian.taxBite < 0 && (
              <p className="flex justify-between text-emerald-600">
                <span>You come out ahead by:</span>
                <span className="font-semibold">${Math.abs(result.civilian.taxBite).toLocaleString()}</span>
              </p>
            )}
            {result.civilian.taxBite > 0 && (
              <p className="flex justify-between text-teal-600">
                <span>Gap to close:</span>
                <span className="font-semibold">+${result.civilian.taxBite.toLocaleString()}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Save Scenario</h3>
              <p className="text-sm text-slate-500">
                Save this calculation as your goal or keep it as a scenario.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setTopPriority(true)
                handleSaveScenarioWithPriority(true)
              }}
              disabled={saving}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-[var(--accent-contrast)] shadow-lg shadow-[var(--accent)]/30 transition hover:bg-[var(--accent-hover)] disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" />
              {saving && topPriority ? 'Saving...' : 'Make This Scenario My New Home'}
            </button>
            <p className="mt-1 text-center text-xs text-slate-400 dark:text-[var(--text-3)]">Sets this as your primary goal on the Dashboard</p>
            <button
              onClick={() => handleSaveScenarioWithPriority(false)}
              disabled={saving}
              className="w-full rounded-full border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-[var(--border)] dark:bg-[var(--surface-2)] dark:text-[var(--text-1)] dark:hover:bg-[var(--surface-3)]"
            >
              {saving && !topPriority ? 'Saving...' : 'Save This Scenario'}
            </button>
            <p className="mt-1 text-center text-xs text-slate-400 dark:text-[var(--text-3)]">Saves for comparison later</p>
          </div>
        </div>
      )}

      {/* Career Map Results — shown after salary calculation for separation mode */}
      {result && !isVeteran && (careerLoading || careerResult) && (
        <div className="mt-10">
          {careerLoading && !careerResult && (
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-8">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              <span className="text-sm text-slate-500">Loading career matches...</span>
            </div>
          )}
          {careerResult && (
            <UnifiedResults
              result={careerResult}
              jobResults={careerJobResults}
              jobsLoading={careerJobsLoading}
              likedJobUrls={likedJobUrls}
              onToggleLike={userId ? handleToggleLike : undefined}
            />
          )}
        </div>
      )}
    </div>
  )
}
